import { SearchFilterTag } from '@/components';
import type {
  ProjectSearchFormType,
  ProjectSearchKeys,
} from '@/features/search/_models/projects-search';
import { t } from '@/i18n';
import { SheetTypeLabelModifier } from '@/utils/enum';
import type { ExtractKeyOf } from '@/utils/utils';
import { type FC, useMemo } from 'react';
import { type FieldPathValue, useFormContext, useWatch } from 'react-hook-form';
import { controlActivityIndicatorByType } from '../../../_utils/activity-indicator-control';
import {
  IndicatorDataNatureTypeModifier,
  IndicatorTermModifier,
  IndicatorTypeModifier,
} from '../../../_utils/enum';

type FieldKey = (typeof ProjectSearchKeys)[number];

type HiddenFieldKey = ExtractKeyOf<
  FieldKey,
  | 'formulaConditions'
  | 'isSearchTargetProjectName'
  | 'q'
  | 'fundSheetType'
  | 'nestedOrganizationIds'
  | 'nestedOtherResponsibleOrganizationIds'
  | 'nestedPolicyIds'
  | 'nestedExpenditureBudgetPurposeIds'
  | 'corporateNumber'
  | 'categories'
  | 'implementationMethods'
  | 'accountingType'
  | 'accountingTypeIds'
  | 'skipAdministrativeReformTeamEvaluation'
  | 'isPublished'
  | 'period'
  | 'inspectionByExternalExpertReviewType'
  | 'isCandidateOfInspectionByExternalExpert'
  | 'lastImplementedFiscalYearPeriod'
>;
const hiddenFields: HiddenFieldKey[] = [
  'formulaConditions',
  'isSearchTargetProjectName',
  'q',
  'fundSheetType',
  'nestedOrganizationIds',
  'nestedOtherResponsibleOrganizationIds',
  'nestedPolicyIds',
  'nestedExpenditureBudgetPurposeIds',
  'corporateNumber',
  'categories',
  'implementationMethods',
  'accountingType',
  'accountingTypeIds',
  'skipAdministrativeReformTeamEvaluation',
  'isPublished',
  'period',
  'inspectionByExternalExpertReviewType',
  'isCandidateOfInspectionByExternalExpert',
  'lastImplementedFiscalYearPeriod',
];

type Props = { field: FieldKey };
const getValue = <K extends FieldKey>(targetValue: any) =>
  targetValue as FieldPathValue<ProjectSearchFormType, K>;

export const CommonFilterTag: FC<Props> = ({ field }) => {
  const { setValue, control } = useFormContext<ProjectSearchFormType>();
  const targetValue = useWatch({ control, name: field });
  const value = useMemo(() => {
    if (!targetValue || hiddenFields.includes(field)) {
      return null;
    }
    switch (field) {
      case 'fiscalYear': {
        const value = getValue<typeof field>(targetValue);
        return t('prefix-fiscal-year-with-value', { value: value?.year() });
      }
      case 'sheetType': {
        const value = getValue<typeof field>(targetValue);
        return SheetTypeLabelModifier[value];
      }
      case 'q': {
        const value = getValue<typeof field>(targetValue);
        return value;
      }
      case 'projectNumbers': {
        const value = getValue<typeof field>(targetValue);
        return `${t('project-number')}: ${value}`;
      }
      case 'indicatorKeyword': {
        const value = getValue<typeof field>(targetValue);
        return `${t('search-keyword-of-activity-indicator')}: ${value}`;
      }
      case 'indicatorKeywordSearchOnlyTitleAndDescription': {
        return t('search-activity-indicator-name');
      }
      case 'indicatorType': {
        const value = getValue<typeof field>(targetValue);
        if (!value) {
          return null;
        }
        return `${t('search-activity-indicator-item')}: ${IndicatorTypeModifier[value]}`;
      }
      case 'hasOnlyQualitativeIndicator': {
        return t('search-has-only-qualitative-indicator');
      }
      case 'indicatorTerm': {
        const value = getValue<typeof field>(targetValue);
        if (!value) {
          return null;
        }
        return `${t('search-activity-indicator-term')}: ${IndicatorTermModifier[value]}`;
      }
      case 'indicatorDataNatureType': {
        const value = getValue<typeof field>(targetValue);
        if (!value) {
          return null;
        }
        return `${t('search-activity-indicator-type')}: ${IndicatorDataNatureTypeModifier[value]}`;
      }
      default: {
        return field;
      }
    }
  }, [field, targetValue]);
  if (!value) {
    return null;
  }
  const isSheetType = field === 'sheetType';
  const isFiscalYear = field === 'fiscalYear';
  return (
    <SearchFilterTag
      label={value}
      closable={!isSheetType && !isFiscalYear}
      onClose={() => {
        const isSetBooleanField =
          field === 'indicatorKeywordSearchOnlyTitleAndDescription' ||
          field === 'hasOnlyQualitativeIndicator';
        if (field === 'indicatorType') {
          controlActivityIndicatorByType({
            // 共通定義を参照しているためanyでOK。
            setValue: setValue as any,
            value: 'reset',
          });
        }
        setValue(field, isSetBooleanField ? false : (null as any));
      }}
    />
  );
};
